import * as React from "react";
import {downloadSchools, searchSchools} from "./schoolsService";
import Loading from "../../components/loading";
import {
    Alert, FormControl,
    FormGroup, IconButton, Input, InputAdornment, InputLabel, Menu, MenuItem,
    Paper, Select, SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableSortLabel,
    Box,
    Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "../../lib/UseDebounce";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import SchoolUpsert from "./schoolUpsert";

export default function SchoolsPage() {
    //
    const [schools, setSchools] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(true);
    const [limit, setLimit] = React.useState(100);
    const [search, setSearch] = React.useState('');
    const [anchorEls, setAnchorEls] = React.useState<any>({});
    const [schoolUpsert, setSchoolUpsert] = React.useState<any>(null as any);
    const [count, setCount] = React.useState(0);
    const [sort, setSort] = React.useState({column: 'name', sortAsc: true});

    //
    React.useEffect(() => {
        document.title = "Schools | DN";
    }, []);

    //
    const reload = async (nLimit: number, sSearch: string, sort: any) => {
        setLoading(true);
        const p = await searchSchools(nLimit, sSearch, sort.column, sort.sortAsc);
        setSchools(p.schools);
        setCount(p.count);
        setLoading(false);
    }

    React.useEffect(() => {
        reload(limit, search, sort).then(x=>{});
    }, []);

    const handleLimit = async (event: SelectChangeEvent) => {
        const l = parseInt(event.target.value as string, 10);
        setLimit(l);
        await reload(l, search, sort);
    };

    useDebounce(() => {
        reload(limit, search, sort).then(x=>{});
        }, [search], 400
    );

    const doSort = async (column: string, defaultAsc: boolean = false) => {
        let s;
        if (sort.column === column) {
            s = {column: column, sortAsc: !sort.sortAsc};
        } else {
            s = {column: column, sortAsc: defaultAsc};
        }

        setSort(s);
        await reload(limit, search, s);
    }

    //
    return (
        <>
            <h2>Schools</h2>
            <FormGroup row={true}>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="items-label">Items</InputLabel>
                    <Select
                        labelId="items-label"
                        value={limit.toString()}
                        label="Items"
                        onChange={handleLimit}
                        size={'small'}
                    >
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                        <MenuItem value={1000}>1000</MenuItem>
                        <MenuItem value={2000}>2000</MenuItem>
                        <MenuItem value={10000}>10000</MenuItem>
                    </Select>
                </FormControl>
                <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }} size="small">
                    <Input
                        id="search"
                        type="text"
                        spellCheck="false"
                        placeholder="Search"
                        value={search || ''}
                        onChange={(e: any) => setSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon /></InputAdornment>}
                    />
                </FormControl>
                {!!schools && !!schools.length && !loading && <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Button aria-label="download" onClick={() => { downloadSchools(search, sort.column, sort.sortAsc) }} variant={'contained'} color={'success'}>
                        <FileDownloadIcon />&nbsp;Export
                    </Button>
                </FormControl>}

                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Button aria-label="Add" onClick={() => { setSchoolUpsert({}) }} variant={'contained'} color={'warning'}>
                        <AddBoxOutlinedIcon />&nbsp;Create School
                    </Button>
                </FormControl>
            </FormGroup> 
            {loading ? <Loading/> :
                <>
                    {!schools || schools.length === 0 ? <Alert severity="warning">No results found</Alert> :
                        <>
                            <Box sx={{margin: '10px 0', fontWeight: 'bold', fontSize: '13px', display:"flex",}}>
                                Showing 1 to {Math.min(schools.length, count).toLocaleString(undefined, {maximumFractionDigits: 0})} of {count.toLocaleString(undefined, {maximumFractionDigits: 0})}.
                            </Box>
                        <TableContainer component={Paper} sx={{maxWidth: 'calc(100vw - 10px)'}}>
                            <Table size="small">
                                <TableHead>
                                    <TableHeaderRow>                                        
                                        <TableCell>
                                            <TableSortLabel
                                                sx={{width: '100%'}}
                                                active={sort.column === 'id'}
                                                direction={sort.column === 'id' && !sort.sortAsc ? 'desc' : 'asc'}
                                                onClick={() => {doSort('id', false)}}
                                            >
                                                Id
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                sx={{width: '100%'}}
                                                active={sort.column === 'name'}
                                                direction={sort.column === 'name' && !sort.sortAsc ? 'desc' : 'asc'}
                                                onClick={() => {doSort('name', true)}}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                sx={{width: '100%'}}
                                                active={sort.column === 'dupeGroup'}
                                                direction={sort.column === 'dupeGroup' && !sort.sortAsc ? 'desc' : 'asc'}
                                                onClick={() => {doSort('dupeGroup', false)}}
                                            >
                                                Dupe Group
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                sx={{width: '100%'}}
                                                active={sort.column === 'billingGroup'}
                                                direction={sort.column === 'billingGroup' && !sort.sortAsc ? 'desc' : 'asc'}
                                                onClick={() => {doSort('billingGroup', false)}}
                                            >
                                                Billing Group
                                            </TableSortLabel>
                                        </TableCell>                                        
                                        <TableCell>
                                            &nbsp;
                                        </TableCell>
                                    </TableHeaderRow>
                                </TableHead>
                                <TableBody>
                                    {schools.map((row: any) => (
                                        <React.Fragment key={row.id}>
                                            <StripedTableRow>
                                            <TableCell>
                                                {row.id}                                                
                                            </TableCell>
                                            <TableCell>
                                                {row.name}
                                            </TableCell>
                                            <TableCell>
                                                {row.dupeGroup.name} ({row.dupeGroup.id})
                                            </TableCell>
                                            <TableCell>
                                                {row.billingGroup.name} ({row.billingGroup.id})
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    aria-haspopup="true"
                                                    onClick={(event) => {
                                                        setAnchorEls({...anchorEls, [row.id]: event.currentTarget});
                                                    }}
                                                >
                                                    <MoreVertIcon sx={{fontSize: '15px'}} />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEls[row.id]}
                                                    open={!!anchorEls[row.id]}
                                                    onClose={() => {
                                                        setAnchorEls({...anchorEls, [row.id]: null});
                                                    }}
                                                >
                                                    <MenuItem onClick={() => {
                                                        setSchoolUpsert(row);
                                                        setAnchorEls({...anchorEls, [row.id]: null});
                                                    }} disableRipple><EditIcon sx={{marginRight: 1}} /> Edit School</MenuItem>
                                                </Menu>
                                            </TableCell>
                                        </StripedTableRow>                                        
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </>
                    }
                </>
            }
            <SchoolUpsert school={schoolUpsert} setSchool={setSchoolUpsert} reload={() => {reload(limit, search, sort).then(() => {})}} />
        </>
    )
}